import { Injectable, signal } from '@angular/core';
import { InventorySummarySearchCommand, InventorySummarySearchResultItem } from '../../models/inventory/inventory-summary-search';
import { LotItemSearchCommand, LotItemSearchResultItem } from '../../models/inventory/lot-item-search';
import { LocationNode } from '../../models/location';
import { LocalHelperService } from '../local-helper.service';

/**
 * View mode type for the items inventory component
 */
export type InventoryViewMode = 'lotItem' | 'inventorySummary';

/**
 * @deprecated Legacy interface for grouped lot items - kept for backward compatibility
 */
export interface GroupLotItem {
  itemCode: string;
  totalNetQuantity: number;
  lotItemsQuantity: number;
}

@Injectable({
  providedIn: 'root',
})
export class ItemsInventoryService {
  // Current view mode
  readonly viewMode = signal<InventoryViewMode>('lotItem');

  // Lot Item view results
  readonly lotItemResults = signal<LotItemSearchResultItem[]>([]);

  // Inventory Summary view results
  readonly inventorySummaryResults = signal<InventorySummarySearchResultItem[]>([]);

  // Search filters for Lot Item view
  readonly lotItemSearchFilters = signal<LotItemSearchCommand>({});

  // Search filters for Inventory Summary view
  readonly inventorySummarySearchFilters = signal<InventorySummarySearchCommand>({});

  // Locations for display names and tree structure
  readonly selectedLocations = signal<LocationNode[]>([]);

  constructor(private localHelperTranslate: LocalHelperService) {}

  /**
   * Set the current view mode
   */
  setViewMode(mode: InventoryViewMode): void {
    this.viewMode.set(mode);
  }

  /**
   * Set lot item search results
   */
  setLotItemResults(results: LotItemSearchResultItem[]): void {
    this.lotItemResults.set(results);
  }

  /**
   * Set inventory summary search results
   */
  setInventorySummaryResults(results: InventorySummarySearchResultItem[]): void {
    this.inventorySummaryResults.set(results);
  }

  /**
   * Set lot item search filters
   */
  setLotItemSearchFilters(filters: LotItemSearchCommand): void {
    this.lotItemSearchFilters.set(filters);
  }

  /**
   * Set inventory summary search filters
   */
  setInventorySummarySearchFilters(filters: InventorySummarySearchCommand): void {
    this.inventorySummarySearchFilters.set(filters);
  }

  /**
   * Set the selected locations (for display name lookup)
   */
  setSelectedLocations(data: LocationNode[]): void {
    this.selectedLocations.set(data);
  }

  /**
   * Get location display name by location code
   */
  getLocationName(locationCode: string): string {
    const location = this.selectedLocations().find((loc) => loc.locationCode === locationCode);
    if (location) {
      return this.localHelperTranslate.getLocalizedValue(location.name);
    }
    return locationCode;
  }

  /**
   * Clear all results
   */
  clearResults(): void {
    this.lotItemResults.set([]);
    this.inventorySummaryResults.set([]);
  }

  /**
   * Clear filters based on current view mode
   */
  clearFilters(): void {
    if (this.viewMode() === 'lotItem') {
      this.lotItemSearchFilters.set({});
    } else {
      this.inventorySummarySearchFilters.set({});
    }
  }
}
